// Language tags Google expects, keyed by the url prefix the sites run on.
const LANGUAGE_TAGS: Record<string, string> = { ee: 'et' }

type HreflangState = { path: string; alternates: Record<string, string> } | null

/**
 * Alternates handed over by the CMS (real translated slugs across nostra.lt / nostra.lv /
 * nostrahome.ee). Stored with the route they belong to, so a stale map from the previous
 * page can never leak into the next one.
 */
export const useCmsHreflang = (alternates: () => Record<string, string> | null | undefined) => {
    const state = useState<HreflangState>('hreflang', () => null)
    const route = useRoute()

    // An empty map is a deliberate "this page has no counterparts" - the route-based fallback
    // must not guess urls for translations that may not exist. A missing one (API that does
    // not send the field yet) keeps the fallback instead of dropping hreflang entirely.
    watchEffect(() => {
        const map = alternates()
        state.value = map ? { path: route.path, alternates: map } : null
    })
}

/**
 * Renders the page's hreflang links: the CMS map when the page provided one, otherwise the
 * same-domain locale urls of the current route.
 */
export const useHreflang = () => {
    const { locales } = useI18n()
    const switchLocalePath = useSwitchLocalePath()
    const route = useRoute()
    const state = useState<HreflangState>('hreflang', () => null)
    const { public: { defaultCountry, defaultLocale, siteUrl } } = useRuntimeConfig()

    const routeAlternates = () => {
        const origin = new URL(siteUrl as string).origin
        const country = (defaultCountry as string).toUpperCase()
        const links: Record<string, string> = {}

        for (const item of locales.value) {
            const code = typeof item === 'string' ? item : item.code
            if (code === 'default') continue

            const url = schemaUrl(switchLocalePath(code as any), origin)
            if (!url) continue

            links[`${LANGUAGE_TAGS[code] ?? code}-${country}`] = url
            if (code === defaultLocale) {
                links['x-default'] = url
            }
        }

        return links
    }

    const links = computed(() => {
        const cms = state.value?.path === route.path ? state.value?.alternates : null
        const map = cms ?? routeAlternates()

        return Object.entries(map).map(([hreflang, href]) => ({
            rel: 'alternate',
            hreflang,
            href,
            key: `hreflang-${hreflang}`,
        }))
    })

    useHead({ link: links })
}
